import { FC, useState } from "react";
import { ApolloError } from "apollo-client";
import { useMutation } from "@apollo/react-hooks";
import classNames from "classnames";

import { Icon } from "@/components";
import { Button, Tooltip, type HeaderActionItem } from "@/ui";
import { AlgoliaOrder, Order, OrderStatusType, Status } from "@/types";
import { useMeta, useNotification } from "@/hooks";
import { MUTATION_UPDATE_ORDER_STATUS } from "apollo/module-operations/order";
import styles from "./OrderActionButtons.module.scss";

const CancelIcon = () => <Icon icon="cancel" />;
const AcceptIcon = () => <Icon icon="accept-rounder" />;
const BackIcon = () => <Icon icon="arrow_left" />;
const CompletedIcon = () => <Icon icon="status-success" />;

interface OrderActionButtonsProps {
    order: Order | AlgoliaOrder;
    className?: string;
    onActionCompleted?: () => void;
    onActionFailed?: () => void;
}

type UseOrderStatusActionsArgs = {
    order: Order | AlgoliaOrder | undefined;
    onActionCompleted?: () => void;
    onActionFailed?: () => void;
};

/** Status-driven order actions as `HeaderActionItem`s (shared by form + modal). */
const useOrderStatusActions = ({
    order,
    onActionCompleted,
    onActionFailed,
}: UseOrderStatusActionsArgs): HeaderActionItem[] => {
    const { user } = useMeta();
    const notify = useNotification();

    const [updatingStatus, setUpdatingStatus] = useState<Status | undefined>(
        undefined,
    );

    const [updateOrderStatus] = useMutation(MUTATION_UPDATE_ORDER_STATUS, {
        onCompleted: () => {
            setTimeout(() => {
                setUpdatingStatus(undefined);
                onActionCompleted?.();
                notify("Order status changed successfully.", "success");
            }, 5000);
        },

        onError: (error: ApolloError) => {
            setUpdatingStatus(undefined);
            onActionFailed?.();
            notify(error.message, "error");
        },
    });

    if (!order || order.status === "ABANDONED_CART") {
        return [];
    }

    const busy = !!updatingStatus;
    const actions: HeaderActionItem[] = [];
    const statusCanBeDeclined: OrderStatusType[] = [
        "PENDING",
        "APPROVED",
        "READY",
        "ASSIGNED",
    ];

    const handleUpdateOrder = (status: Status) => {
        setUpdatingStatus(status);
        updateOrderStatus({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    id:
                        (order as Order).entity_id ||
                        (order as AlgoliaOrder).objectID,
                    status: status,
                },
            },
        });
    };

    if (
        order.status === "APPROVED" ||
        order.status === "READY" ||
        order.status === "DECLINED" ||
        order.status === "ASSIGNED"
    ) {
        actions.push({
            id: "PENDING",
            label: "Back to processing",
            icon: <BackIcon />,
            tooltip: "Move this order back to processing",
            disabled: busy,
            loading: updatingStatus === "PENDING",
            variant: "secondary",
            className: styles.toneSoft,
            onClick: () => handleUpdateOrder("PENDING"),
        });
    }

    if (order.status === "APPROVED" || order.status === "READY") {
        actions.push({
            id: "DELIVERED",
            label: "Completed",
            icon: <CompletedIcon />,
            tooltip: "Mark this order as completed",
            disabled: busy,
            loading: updatingStatus === "DELIVERED",
            variant: "primary",
            onClick: () => handleUpdateOrder("DELIVERED"),
        });
    }

    if (statusCanBeDeclined.includes(order.status)) {
        actions.push({
            id: "DECLINED",
            label: "Reject order",
            icon: <CancelIcon />,
            tooltip: "Reject this order",
            disabled: busy,
            loading: updatingStatus === "DECLINED",
            variant: "secondary",
            destructive: true,
            onClick: () => handleUpdateOrder("DECLINED"),
        });
    }

    if (order.status === "PENDING") {
        actions.push({
            id: "APPROVED",
            label: "Approve order",
            icon: <AcceptIcon />,
            tooltip: "Approve this order",
            disabled: busy,
            loading: updatingStatus === "APPROVED",
            variant: "primary",
            onClick: () => handleUpdateOrder("APPROVED"),
        });
    }

    return actions;
};

const OrderActionButtons: FC<OrderActionButtonsProps> = ({
    order,
    className,
    onActionCompleted,
    onActionFailed,
}) => {
    const actions = useOrderStatusActions({
        order,
        onActionCompleted,
        onActionFailed,
    });

    if (!actions.length) {
        return null;
    }

    return (
        <div
            className={classNames(
                styles.root,
                "order_action__buttons",
                className,
            )}
        >
            {actions.map(action => {
                const button = (
                    <Button
                        className={action.className}
                        disabled={action.disabled || action.loading}
                        iconLeft={action.loading ? undefined : action.icon}
                        key={action.id}
                        loading={action.loading}
                        onClick={action.onClick}
                        type="button"
                        variant={action.variant ?? "primary"}
                    >
                        {action.label}
                    </Button>
                );

                if (action.tooltip) {
                    return (
                        <Tooltip
                            content={action.tooltip}
                            key={action.id}
                            side="top"
                        >
                            {button}
                        </Tooltip>
                    );
                }

                return button;
            })}
        </div>
    );
};

export { OrderActionButtons, useOrderStatusActions };
